import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../lib/supabaseClient";
import { useAuth } from "../../context/AuthContext.jsx";
import Celebration from "../../components/Celebration.jsx";

const LEVEL_ID = "off-the-clock";

function computeOutcome(observationResult, plan, buildResult) {
  const { log } = buildResult;

  const observationScore = observationResult.correctCount * 15;
  const purposeBonus = plan.purposeQuality === "sharp" ? 100 : 0;

  const hourValues = Object.values(plan.hours);
  const hoursBalanceBonus = hourValues.every((v) => v > 0) ? 50 : 0;

  const goodChoices = log.filter((l) => l.choice.tag === "good").length;
  const badChoices = log.filter((l) => l.choice.tag === "bad").length;
  const choiceScore = goodChoices * 20 - badChoices * 20;

  const shipped = log.some((l) => l.choice.id === "ship");
  const artifactBonus = shipped ? 100 : 0;

  const score = Math.max(50, observationScore + purposeBonus + hoursBalanceBonus + choiceScore + artifactBonus + 50);

  let verdict;
  if (goodChoices >= 5 && shipped) {
    verdict = { label: "Built a Small Piece of Leverage", tone: "lime" };
  } else if (goodChoices >= 3) {
    verdict = { label: "Making Real Progress", tone: "gold" };
  } else {
    verdict = { label: "Mostly Still on the Treadmill", tone: "danger" };
  }

  return { observationScore, purposeBonus, hoursBalanceBonus, choiceScore, artifactBonus, goodChoices, badChoices, shipped, verdict, score };
}

export default function DebriefStage({ observationResult, plan, buildResult, onRetry }) {
  const navigate = useNavigate();
  const { user, profile, refreshProfile } = useAuth();
  const [saving, setSaving] = useState(true);
  const [saved, setSaved] = useState(false);

  const outcome = computeOutcome(observationResult, plan, buildResult);
  const didLevelUp = Math.floor(((profile?.xp ?? 0) + outcome.score) / 500) + 1 > (profile?.level ?? 1);

  useEffect(() => {
    let cancelled = false;

    async function save() {
      if (!user) {
        setSaving(false);
        return;
      }
      const newXp = (profile?.xp ?? 0) + outcome.score;
      const newLevel = Math.max(profile?.level ?? 1, Math.floor(newXp / 500) + 1);
      const newNetWorth = (profile?.net_worth_cents ?? 0) + outcome.score * 100;

      const { error } = await supabase
        .from("profiles")
        .update({ xp: newXp, level: newLevel, net_worth_cents: newNetWorth })
        .eq("id", user.id);

      if (!cancelled) {
        if (!error) {
          await refreshProfile();
          const { error: challengeError } = await supabase.rpc("record_level_result", {
            p_level_id: LEVEL_ID,
            p_score: outcome.score,
          });
          if (challengeError) {
            console.error("Failed to record challenge result:", challengeError.message);
          }
          setSaved(true);
        } else {
          console.error("Failed to save Off The Clock results:", error.message);
        }
        setSaving(false);
      }
    }

    save();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toneClasses = {
    lime: "border-lime bg-lime/10 text-lime",
    gold: "border-gold bg-gold/10 text-gold",
    danger: "border-danger bg-danger/10 text-danger",
  };

  return (
    <div className="animate-popIn text-center">
      <Celebration show={saved} levelUp={didLevelUp} />
      <h2 className="pixel-heading mb-1 text-lg text-lime">EMPIRE CHECK</h2>
      <p className="mb-6 text-xs uppercase tracking-widest text-mist/50">Day 90</p>

      <div className={`mx-auto mb-4 max-w-sm border-2 p-5 ${toneClasses[outcome.verdict.tone]}`}>
        <p className="label-pixel mb-1">Verdict</p>
        <p className="font-display text-lg">{outcome.verdict.label}</p>
      </div>

      <div className="mx-auto mb-6 max-w-sm space-y-2 border-2 border-black bg-ink p-4 text-left text-sm">
        <div className="flex justify-between">
          <span className="text-mist/60">Patterns Read Correctly</span>
          <span className="text-cyan">{observationResult.correctCount}/6</span>
        </div>
        <div className="flex justify-between">
          <span className="text-mist/60">Purpose Statement</span>
          <span className={plan.purposeQuality === "sharp" ? "text-lime" : "text-gold"}>
            {plan.purposeQuality === "sharp" ? "Sharp" : "Vague"}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-mist/60">Shipped an Artifact?</span>
          <span className={outcome.shipped ? "text-lime" : "text-danger"}>{outcome.shipped ? "Yes" : "Not yet"}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-mist/60">Good Calls / Rough Calls</span>
          <span>
            <span className="text-lime">{outcome.goodChoices}</span>
            <span className="text-mist/40"> / </span>
            <span className="text-danger">{outcome.badChoices}</span>
          </span>
        </div>
        <div className="relative flex justify-between border-t border-black/50 pt-2 font-display text-[11px]">
          <span className="text-mist/60">SCORE</span>
          <span className="text-gold">{outcome.score.toLocaleString()}</span>
          {saved && (
            <span className="absolute -top-4 right-0 animate-floatUp text-[9px] text-lime">+{outcome.score} XP</span>
          )}
        </div>
      </div>

      <p className="mx-auto mb-6 max-w-sm text-[11px] text-mist/50">
        None of this required money to start — it required noticing, direction, and enough
        consistency to actually ship something. That's what turns hours into leverage instead of
        just more hours.
      </p>

      <p className="mb-6 text-xs text-mist/50">
        {saving ? "Saving to your profile…" : saved ? "Net worth and XP updated on your profile ✅" : "Results ready."}
      </p>

      <div className="flex flex-wrap justify-center gap-3">
        <button onClick={onRetry} className="btn-magenta">
          ↻ RUN ANOTHER 90 DAYS
        </button>
        <button onClick={() => navigate("/")} className="btn-cyan">
          LEVEL SELECT
        </button>
      </div>
    </div>
  );
}
